import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import Footer from '../../components/Footer';
import NotificationBanner from '../../components/NotificationBanner';
import { AuthContext, AppSettingsContext } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { BookOpen, Calendar, Eye, ArrowRight, Tag, Rocket, TrendingUp } from 'lucide-react';
import { useDateFormat } from '../../contexts/DateFormatContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const { formatDate } = useDateFormat();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [page, selectedCategory]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 9 };
      if (selectedCategory) params.category = selectedCategory;
      
      const response = await axios.get(`${API}/blog/articles`, { params });
      setArticles(response.data.articles);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const ArticleCard = ({ article }) => (
    <Link to={`/blog/${article.slug}`} className="block group">
      <Card className="glass-dark border-white/10 overflow-hidden h-full hover:border-blue-500/50 transition-all duration-300">
        {article.featured_image && (
          <div className="h-48 overflow-hidden">
            <img 
              src={article.featured_image} 
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
              {article.category}
            </span>
          </div>
          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-gray-400 text-sm mb-4 line-clamp-3">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(article.published_at)}
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {article.views} views
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  const CTACard = () => (
    <Card className="glass border-green-500/30 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500" />
      <CardContent className="p-6 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 mb-4">
          <Rocket className="w-7 h-7 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Start Tracking Your Bets</h3>
        <p className="text-gray-400 text-sm mb-4">
          Join thousands of bettors who use our AI-powered tools to improve their betting strategy.
        </p>
        <Link to="/signup">
          <Button className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white">
            Get Started Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
        <p className="text-xs text-gray-500 mt-3">No credit card required</p>
      </CardContent>
    </Card>
  );

  const pageContent = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Notification Banner - only for logged in users */}
        {user && <NotificationBanner type="blogs" />}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30">
              <BookOpen className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">Blog</h1>
              <p className="text-gray-400">Football betting insights, tips, and analysis</p>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => { setSelectedCategory(null); setPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !selectedCategory 
                ? 'bg-blue-500 text-white' 
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            All Articles
          </button>
          {categories.map(cat => (
            <button
              key={cat.name}
              onClick={() => { setSelectedCategory(cat.name); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat.name
                  ? 'bg-blue-500 text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {cat.name} ({cat.count})
            </button>
          ))}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Articles Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
                <p className="text-gray-400 mt-4">Loading articles...</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-20">
                <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Articles Yet</h3>
                <p className="text-gray-400">Check back soon for new content!</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map(article => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      Previous
                    </Button>
                    <span className="px-4 py-2 text-gray-400">
                      Page {page} of {pagination.total_pages}
                    </span>
                    <Button
                      onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                      disabled={page === pagination.total_pages}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Show CTA only for guests */}
            {!user && <CTACard />}

            {/* Popular Tags */}
            {articles.length > 0 && (
              <Card className="glass-dark border-white/10">
                <CardContent className="p-5">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-blue-400" />
                    Popular Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(articles.flatMap(a => a.tags || []))].slice(0, 10).map(tag => (
                      <span 
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs bg-white/10 text-gray-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // If user is logged in, use Layout component
  if (user) {
    return (
      <Layout>
        {pageContent}
      </Layout>
    );
  }

  // Public view with marketing-style navigation (like BecomeTipster)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Public Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              {appSettings?.app_logo_url ? (
                <img 
                  src={appSettings.app_logo_url} 
                  alt={appSettings?.app_name || 'BettingTracker'} 
                  className="h-10 max-w-[200px] object-contain"
                />
              ) : (
                <>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-lg sm:text-xl font-bold whitespace-nowrap">{appSettings?.app_name || 'BettingTracker'}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => navigate('/login')} 
                variant="ghost"
                className="text-gray-300 hover:text-white"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/signup')} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content with top padding for fixed nav */}
      <div className="pt-16">
        {pageContent}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Blog;
